import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { readFileAsBase64 } from '../files.ts';
import { DRY_RUN_DATA_IMPORT } from '../queries.ts';
import { DryRunInput, DryRunResult } from '../types.ts';

interface DryRunRequest {
    lookupDefinitionId: string;
    maxRows: number;
    transformerConfig: string;
    input: DryRunInput;
}

const inputVariables = async (input: DryRunInput) => {
    if (input.mode === 'SFTP') {
        return { sftpFiles: input.sftpFiles };
    }
    if (input.mode === 'UPLOAD') {
        const files = await Promise.all(
            input.files.map(async ({ file, fileNamePattern }) => ({
                name: file.name,
                contentBase64: await readFileAsBase64(file),
                fileNamePattern,
            }))
        );
        return { files };
    }
    return {};
};

const useDryRunDataImport = () => {
    const [runMutation] = useMutation<{ dryRunDataImport: DryRunResult }>(DRY_RUN_DATA_IMPORT, {
        fetchPolicy: 'no-cache',
    });
    const [result, setResult] = useState<DryRunResult | null>(null);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);

    const runDryRun = async ({ lookupDefinitionId, maxRows, transformerConfig, input }: DryRunRequest) => {
        setIsRunning(true);
        setRequestError(null);
        try {
            const { data } = await runMutation({
                variables: { lookupDefinitionId, maxRows, transformerConfig, ...(await inputVariables(input)) },
            });
            setResult(data?.dryRunDataImport ?? null);
        } catch (error) {
            setResult(null);
            setRequestError((error as Error).message);
        } finally {
            setIsRunning(false);
        }
    };

    const resetDryRun = () => {
        setResult(null);
        setRequestError(null);
    };

    return { runDryRun, resetDryRun, result, requestError, isRunning };
};

export default useDryRunDataImport;
